// The server implementation of DataClient (API.md section 10): same interface as the sample client, so no screen
// changes. Collections load whole (?all=1) because pages filter in memory today; forced page states (?state=) behave
// exactly as in the sample client so checks and reviews work the same in both modes.
import type { CollectionName, DataClient, Patch, Row } from "./client";
import { forcedState, type ForcedState } from "./client";
import { signInHref } from "@/app/safe-next";

const BASE = "/api/v1";
/** The API's address prefix, for the few places a browser element fetches a server file directly (a voicemail's audio preview). */
export const API_BASE = BASE;
const tz = (() => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return "UTC";
  }
})();

/** A failed API call, carrying the problem+json body. */
export class ApiError extends Error {
  constructor(readonly status: number, readonly code: string, readonly body: unknown) {
    super(`${status} ${code}`);
  }
}

let signingIn: Promise<void> | null = null;
/** Development only: without a real sign-in, sign in as the workspace owner (sam), so a preview shows everything (operator, 2026-10-19). VITE_DEV_PERSON overrides. */
async function devSignIn() {
  const personId = (import.meta.env.VITE_DEV_PERSON as string | undefined) ?? "sam";
  const res = await fetch("/api/dev/sign-in-as", { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ personId }) });
  if (!res.ok) throw new ApiError(res.status, "dev_sign_in_failed", await res.text());
}

// development: sign in once before the first call, so a fresh browser never logs a 401
let ready: Promise<void> | null = null;

/** "Preview as" (Settings, Access): the role or person an owner is looking through, kept for this tab only. The
 *  server honours it for owners and refuses every write while it is set. */
export type Preview = { kind: "role" | "person"; id: string; label: string };
const PREVIEW_KEY = "offsilo.preview";
export function currentPreview(): Preview | null {
  try {
    const raw = sessionStorage.getItem(PREVIEW_KEY);
    return raw ? (JSON.parse(raw) as Preview) : null;
  } catch {
    return null;
  }
}
/** Starts or ends a preview, then reloads so every screen reads through it (a client contact opens the portal). */
export function setPreview(next: Preview | null, to = "/home") {
  try {
    if (next) sessionStorage.setItem(PREVIEW_KEY, JSON.stringify(next));
    else sessionStorage.removeItem(PREVIEW_KEY);
  } catch {
    // storage blocked: the preview simply does not start
  }
  window.location.assign(next ? to : "/settings/access/preview-as");
}
// While a practice tour runs, every call says so: the server logs the rows it makes, and "Remove practice data" removes them.
let practiceRun = false;
export function setPracticeRun(on: boolean): void {
  practiceRun = on;
}
const previewHeaders = (): Record<string, string> => {
  const p = currentPreview();
  return p ? { [p.kind === "role" ? "x-preview-role" : "x-preview-person"]: p.id } : {};
};

/** Path prefixes the shell reads (sidebar and rail layout, boot, preferences, workspace). A forced page state never touches them,
 *  so the shell renders around every forced page (the page state is for the page, not the chrome). */
export const CHROME_PATHS: readonly string[] = ["/workspace", "/me", "/auth", "/settings"];
const underPath = (path: string, prefix: string) => {
  const base = path.split("?")[0] ?? "";
  return base === prefix || base.startsWith(`${prefix}/`);
};
/** The forced state a call answers with: writes are never forced, shell paths stay ready, every other read follows ?state=. */
export function forcedFor(path: string, method: string): ForcedState {
  if (method.toUpperCase() !== "GET") return "ready";
  if (CHROME_PATHS.some((p) => underPath(path, p))) return "ready";
  return forcedState();
}

export async function call<T>(path: string, init: RequestInit = {}, retried = false, shell = false): Promise<T> {
  // a forced loading state never answers, and a forced error throws before any request is sent (only loading and error act here;
  // empty and partial are shaped by the DataClient, since a raw response cannot be emptied generically)
  const forced = shell ? "ready" : forcedFor(path, init.method ?? "GET");
  if (forced === "loading") return new Promise<T>(() => {});
  if (forced === "error") throw new ApiError(503, "forced", { message: "The server did not answer. This is the forced error state." });
  if (import.meta.env.DEV) await (ready ??= devSignIn().catch(() => {}));
  const res = await fetch(`${BASE}${path}`, {
    ...init,
    credentials: "same-origin",
    headers: { "x-time-zone": tz, ...previewHeaders(), ...(practiceRun ? { "x-offsilo-practice": "1" } : {}), ...(init.body ? { "content-type": "application/json" } : {}), ...(init.headers ?? {}) },
  });
  if (res.status === 401 && !retried && import.meta.env.DEV) {
    signingIn ??= devSignIn().finally(() => {
      signingIn = null;
    });
    await signingIn;
    return call<T>(path, init, true, shell);
  }
  // a built app with no session goes to its own sign-in page: a client on the portal to /portal/sign-in, the staff app to
  // /sign-in (public pages never call the API this way); never from a sign-in page itself (X5-18)
  if (res.status === 401 && !import.meta.env.DEV && typeof window !== "undefined") {
    const here = window.location.pathname;
    if (!here.startsWith("/sign-in") && !here.startsWith("/portal/sign-in")) {
      const portal = here.startsWith("/portal") || path.startsWith("/portal");
      window.location.assign(signInHref(portal ? "/portal/sign-in" : "/sign-in", `${here}${window.location.search}`));
      return new Promise<T>(() => {});
    }
  }
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new ApiError(res.status, (body as { code?: string }).code ?? "error", body);
  }
  return (await res.json()) as T;
}

const never = <T>() => new Promise<T>(() => {});

// the server's plain project list leaves archived projects out (A053); the app keeps them and hides them in the Projects page
// fg (X13-0101a) is the server-side filter tree, base64url JSON, which the server applies before the page filters locally
export function listPath(collection: string, fg?: string, archived = false): string {
  const parts = ["all=1"];
  if (collection === "projects") parts.push("archived=include");
  if (archived && collection === "notifications") parts.push("archived=true");
  if (archived && collection === "contacts") parts.push("archived=1");
  if (fg) parts.push(`fg=${encodeURIComponent(fg)}`);
  return `/${collection}?${parts.join("&")}`;
}

export function createServerClient(): DataClient {
  const forced = async (chrome = false) => {
    const state = chrome ? "ready" : forcedState();
    if (state === "loading") await never();
    if (state === "error") throw new Error("The server did not answer. This is the forced error state.");
    return state;
  };
  return {
    async workspace() {
      return call("/workspace");
    },
    async metrics(week?: "this" | "last" | "next") {
      await forced();
      return call(week && week !== "this" ? `/metrics?week=${week}` : "/metrics");
    },
    async list<C extends CollectionName>(collection: C, opts?: { chrome?: boolean; fg?: string; archived?: boolean }) {
      const state = await forced(opts?.chrome);
      const { rows } = await call<{ rows: Row<C>[] }>(listPath(collection, opts?.fg, opts?.archived), {}, false, opts?.chrome);
      if (state === "empty") return [];
      if (state === "partial") return rows.slice(0, Math.ceil(rows.length / 4));
      return rows;
    },
    async get<C extends CollectionName>(collection: C, id: string) {
      await forced();
      try {
        return await call<Row<C>>(`/${collection}/${encodeURIComponent(id)}`);
      } catch (e) {
        if (e instanceof ApiError && e.status === 404) return undefined;
        throw e;
      }
    },
    async update<C extends CollectionName>(patch: Patch<C>) {
      await forced();
      const res = await call<{ row: Row<C>; inverse: Partial<Row<C>> }>(`/${patch.collection}/${encodeURIComponent(patch.id)}`, {
        method: "PATCH",
        body: JSON.stringify({ changes: patch.changes }),
      });
      return { collection: patch.collection, id: patch.id, changes: res.inverse };
    },
    async create<C extends CollectionName>(collection: C, row: Omit<Row<C>, "id"> & { id?: string }) {
      await forced();
      return call<Row<C>>(`/${collection}`, { method: "POST", body: JSON.stringify(row) });
    },
    async remove<C extends CollectionName>(collection: C, id: string) {
      await forced();
      return call<Row<C>>(`/${collection}/${encodeURIComponent(id)}`, { method: "DELETE" });
    },
  };
}
